import type { ActivityTracker } from "./activityTracker";

/** Mínimo entre dos `markInput` consecutivos: los eventos de mouse/scroll disparan decenas
 *  por segundo y alcanza con saber "hubo actividad en los últimos 2 s". */
export const INPUT_THROTTLE_MS = 2_000;

/**
 * Conecta el tracker con el DOM (input, visibilidad, cierre de pestaña, errores). Devuelve la
 * función que desconecta todo. Todos los listeners son pasivos y a prueba de excepciones.
 */
export function bindActivityDom(tracker: ActivityTracker): () => void {
  let lastInput = 0;
  const onInput = () => {
    const now = Date.now();
    if (now - lastInput < INPUT_THROTTLE_MS) return;
    lastInput = now;
    tracker.markInput();
  };
  const onVisibility = () => {
    if (document.visibilityState === "hidden") tracker.onVisibilityHidden();
    else onInput();
  };
  const onPageHide = () => tracker.onPageHide();
  const onPageShow = (e: PageTransitionEvent) => tracker.onPageShow(e.persisted);
  // Sin mensaje ni stack: solo el código cerrado.
  const onRejection = () => tracker.trackError("unhandled_rejection");

  const inputEvents = ["pointerdown", "keydown", "wheel", "touchstart", "mousemove"] as const;
  inputEvents.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
  window.addEventListener("scroll", onInput, { passive: true, capture: true });
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("unhandledrejection", onRejection);

  return () => {
    inputEvents.forEach((e) => window.removeEventListener(e, onInput));
    window.removeEventListener("scroll", onInput, { capture: true });
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    window.removeEventListener("unhandledrejection", onRejection);
  };
}
